// AdminGate (UX-041/042, mockup M5) — the ONE screen every admin page shows
// instead of its own "access required" card or generic "Failed to load" banner.
//
// It only EXPLAINS a decision made elsewhere. The role check stays in each page
// (and in the API); this component never grants or widens anything.
//
//   permission  — the caller is not an owner/admin: say so, say who to ask, and
//                 give a working way out (Back to Home).
//   load failed — a request errored. 403 = no permission, 404 = not set up yet,
//                 anything else = a real failure with a Try again. The page must
//                 render this INSTEAD of its normal body, so no Invite button,
//                 seat bar or "no members yet" empty state ever sits next to an
//                 outage.

import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { gateKindFor, httpStatusOf, type GateKind } from './gateStatus'
import { ShieldAlert, AlertCircle, Home, RefreshCw } from 'lucide-react'

interface AdminGateProps {
  /** What the page manages, as a lowercase noun phrase: "members and licences". */
  area: string
  orgName?: string
  /** Present when a load failed; absent for the plain no-permission case. */
  error?: unknown
  onRetry?: () => void
  /** Platform admin with no organisation selected: offer the organisation list. */
  isPlatformAdmin?: boolean
  /** Replace the generated heading / body (used by non-organisation gates). */
  title?: string
  description?: ReactNode
}

export default function AdminGate({
  area,
  orgName,
  error,
  onRetry,
  isPlatformAdmin,
  title,
  description,
}: AdminGateProps) {
  const kind: GateKind = error === undefined ? 'permission' : gateKindFor(error)
  const forOrg = orgName ? ` for ${orgName}` : ''
  const status = httpStatusOf(error)

  let heading: string
  let body: ReactNode
  switch (kind) {
    case 'permission':
      heading = `You can't manage ${area}${forOrg}`
      body = (
        <>
          This page is for owners and admins of {orgName || 'this organisation'}. Ask an owner of{' '}
          {orgName || 'the organisation'} to give you access.
        </>
      )
      break
    case 'forbidden':
      heading = `You don't have permission to see ${area}${forOrg}`
      body = <>Error 403: your role in {orgName || 'this organisation'} doesn't include this page.</>
      break
    case 'not-set-up':
      heading = `${area.charAt(0).toUpperCase()}${area.slice(1)} ${orgName ? `isn't set up for ${orgName} yet` : "isn't set up yet"}`
      body = <>Error 404: nothing has been created here yet. An owner or admin can set it up.</>
      break
    default:
      heading = `We couldn't load ${area}`
      body = (
        <>
          {status ? `Error ${status}: ` : ''}something went wrong on our side. Nothing you can see
          here has been changed.
        </>
      )
  }

  const Icon = kind === 'failed' ? AlertCircle : ShieldAlert

  return (
    <div className="flex items-center justify-center py-16 px-4" data-testid="admin-gate" data-gate-kind={kind}>
      <div className="light-card p-8 max-w-md w-full text-center">
        <div className="w-14 h-14 rounded-full bg-amber-500/20 text-amber-700 flex items-center justify-center mx-auto mb-4">
          <Icon size={26} aria-hidden />
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">{title ?? heading}</h2>
        <p className="text-sm text-gray-500">{description ?? body}</p>
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-iflow-700 hover:bg-iflow-800 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            <Home size={16} aria-hidden /> Back to Home
          </Link>
          {kind === 'failed' && onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex items-center gap-2 bg-white text-iflow-700 border border-iflow-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-iflow-50"
            >
              <RefreshCw size={16} aria-hidden /> Try again
            </button>
          )}
          {kind === 'permission' && isPlatformAdmin && (
            <Link
              to="/admin/organisations"
              className="inline-flex items-center gap-2 bg-white text-iflow-700 border border-iflow-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-iflow-50"
            >
              Choose an organisation
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}
